<template>
  <div class="song-block bc-black p">
    <div
      class="song-cover p-rel"
      :class="{ 'is-active': isCurrentlyPlaying }"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @click="onCardClick"
    >
      <img
        v-if="song.cover"
        :src="song.cover"
        :class="{ hovermask: hovered, spinCoverAni: spinCover }"
        alt=""
      />
      <div v-else class="cover-placeholder d-f ai-c jc-c">
        <IconBase :w="15" :h="15"><IconMusic /></IconBase>
      </div>

      <!-- Hover / tap overlay -->
      <div class="song-overlay" :class="{ 'song-overlay--visible': showOverlay, 'pointer': song.audio }" @click.stop="song.audio && handlePlay()">
        <p v-if="song.audio" class="overlay-play link tt-up" :class="hoveredLink ? 'overlay-play--dim' : (hovered ? 'underline' : '')">
          Play
        </p>
        <a
          v-for="({ label, url }, i) in platformLinks"
          :key="i"
          :href="url"
          target="_blank"
          rel="noopener"
          class="overlay-link tt-up"
          @mouseenter="hoveredLink = true"
          @mouseleave="hoveredLink = false"
          @click.stop="mobileOverlay = false"
        >{{ label }}<IconBase :w="10" :h="10"><IconArrowUpRight /></IconBase></a>
      </div>

      <!-- Buffering -->
      <span class="buffering-icon" v-if="isCurrentlyPlaying && playerIsLoading">
        <BufferingIcon animate="beat" class="tw-text-white tw-cursor-pointer" w="30" h="30" />
      </span>
    </div>

    <!-- Title -->
    <p class="song-title bs-bb m-t">{{ song.title }}</p>
    <p v-if="artistParts.length" class="song-artist bs-bb">
      <template v-for="(part, i) in artistParts">
        <span v-if="i > 0" :key="'x'+i" class="song-artist__sep"> x </span>
        <router-link v-if="part.url && part.internal" :key="'r'+i" :to="part.url" class="song-artist__link">{{ part.name }}</router-link>
        <a v-else-if="part.url" :key="'a'+i" :href="part.url" target="_blank" rel="noopener" class="song-artist__link">{{ part.name }}</a>
        <span v-else :key="'n'+i">{{ part.name }}</span>
      </template>
    </p>

    <div v-if="editButtons">
      <a class="link font-small2 mute tt-up" @click.stop="editDrawerOpen = true">Edit</a>
    </div>

    <EditTrackDrawer
      v-if="editDrawerOpen"
      :song="song"
      @close="editDrawerOpen = false"
      @saved="editDrawerOpen = false"
    />
  </div>
</template>

<script>
import PlayerMixin from '@/mixins/PlayerMixin';
import IconBase from '@/assets/icons/IconBase.vue';
import IconMusic from '@/assets/icons/IconMusic.vue';
import IconArrowUpRight from '@/assets/icons/IconArrowUpRight2.vue';
import BufferingIcon from 'vue-ionicons/dist/ios-play-circle';
import EditTrackDrawer from '@/components/mymusic/EditTrackDrawer.vue';

const PLATFORM_LABELS = {
  spotify: 'Spotify',
  youtube: 'YouTube',
  soundcloud: 'SoundCloud',
  bandcamp: 'Bandcamp',
  appleMusic: 'Apple Music',
  beatport: 'Beatport',
};

export default {
  name: 'SongBlockProfile',
  components: { IconBase, IconMusic, IconArrowUpRight, BufferingIcon, EditTrackDrawer },
  mixins: [PlayerMixin],
  props: ['song', 'count', 'playlistPosition', 'editButtons', 'artistHandle'],
  data() {
    return {
      hovered: false,
      hoveredLink: false,
      mobileOverlay: false,
      spinCover: false,
      editDrawerOpen: false,
    };
  },
  computed: {
    isCurrentlyPlaying() {
      return (
        this.activePlayer.currentTrackId === this.count &&
        this.activePlaylist.position === this.playlistPosition
      );
    },
    platformLinks() {
      const links = this.song?.links || {};
      return Object.entries(links).map(([key, val]) => {
        const url = typeof val === 'object' ? val.url : val;
        const label = (typeof val === 'object' && val.title) || PLATFORM_LABELS[key] || key;
        return { label, url };
      });
    },
    artistParts() {
      const parts = [];
      if (this.song?.artist) {
        parts.push({ name: this.song.artist, url: this.artistHandle ? `/${this.artistHandle}` : null, internal: true });
      }
      const credits = this.song?.credits || {};
      Object.values(credits).forEach(c => {
        if (c.name) parts.push({ name: c.name, url: c.link || null, internal: false });
      });
      return parts;
    },
    hasOverlayContent() {
      return this.song?.audio || this.platformLinks.length > 0;
    },
    showOverlay() {
      return (this.hovered || this.mobileOverlay) && this.hasOverlayContent;
    },
  },
  methods: {
    onMouseEnter() {
      this.hovered = true;
      this.mobileOverlay = false;
    },
    onMouseLeave() {
      this.hovered = false;
    },
    spinCoverFunc() {
      this.spinCover = true;
      setTimeout(() => { this.spinCover = false; }, 1500);
    },
    onCardClick() {
      if (this.hovered) return;
      if (this.hasOverlayContent) {
        this.mobileOverlay = !this.mobileOverlay;
      } else {
        this.handlePlay();
      }
    },
    handlePlay() {
      this.$emit('play-item');
      this.spinCoverFunc();
      this.mobileOverlay = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/_variables.scss";
@import "@/scss/_classes.scss";

.song-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: $mg-desktop-lg;
  width: 40vh;
  box-sizing: border-box;
  @media #{$mobile} {
    width: 100%;
  }
}

.song-cover {
  width: 100%;
  height: 0;
  padding-bottom: 100%;

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.cover-placeholder {
  position: absolute;
  inset: 0;
  background-color: rgba($white, 0.06);
  color: rgba($white, 0.3);
}

.song-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  opacity: 0;
  transition: opacity 0.18s ease;
  pointer-events: none;

  &--visible {
    opacity: 1;
    pointer-events: auto;
  }
}

.overlay-play {
  background: none;
  border: none;
  color: $white;
  font-size: inherit;
  font-family: inherit;
  cursor: pointer;
  padding: 0;

  &--dim {
    color: $white-sec;
    text-decoration: none;
  }
}

.overlay-link {
  display: flex;
  align-items: center;
  gap: 3px;
  color: $white-sec;
  text-decoration: none;
  font-size: $p1;

  &:hover {
    color: $white;
  }
}

.song-title {
  width: 100%;
  text-align: center;
  overflow-wrap: break-word;
  word-break: break-word;
}

.song-artist {
  width: 100%;
  text-align: center;
  overflow-wrap: break-word;
  word-break: break-word;
  color: $white-sec;

  &__sep {
    opacity: 0.6;
  }

  &__link {
    color: inherit;
    text-decoration: none;
    &:hover { text-decoration: underline; text-underline-offset: 2px; }
  }
}

.buffering-icon {
  position: absolute;
  bottom: $mg-desktop-sm;
  right: $mg-desktop-sm;
  pointer-events: none;
}
</style>
